import Device from '@models/Device';
import { DEVICE_TYPE, useDeviceStore } from '#src/store/device';
import useLogger from 'composables/logger';
import { isReplacement } from '#actions/skus';

export enum SKU_TYPE {
  ALL_NON_DEVICE = 'ALL_NON_DEVICE',
  ASSET = 'ASSET',
  ASSET_REPLACEMENT = 'ASSET_REPLACEMENT',
  DASHCAM = 'DASHCAM',
  DASHCAM_REPLACEMENT = 'DASHCAM_REPLACEMENT',
  DEMO = 'DEMO',
  MULTIPLE = 'MULTIPLE',
  NEW = 'NEW',
  NON_DEVICE = 'NON_DEVICE',
  PILOT = 'PILOT',
  REPLACEMENT = 'REPLACEMENT',
  TRIAL = 'TRIAL',
  UNKNOWN = 'UNKNOWN',
  UPGRADE = 'UPGRADE',
}

export const NEW_ORDER_SKUS = [
  'BUNDLEXT2469BCY',
  'EZNATIONZUB50',
  'EZZFC3P',
  'FZ30024M',
  'J1939',
  'NATIONZUB',
  'RENTALPREMIUM',
  'RENTALPREMIUMYC',
  'SPOT',
  'FZ30012M',
  'XT25J19',
  'ZFC1P',
  'ZFC1S',
  'ZFC2P',
  'ZFC2S',
  'ZFC3P',
  'ZFC3S',
  'ZFC1L',
  'ZFC1SHW',
  'ZFC3L',
  'ZK30012M',
  'ZRC1P',
  'ZRC2P',
  'ZRCLULA',
];

export const NON_DEVICE_SKUS = [
  'EZPay10',
  'OBDCABLE',
  'Phone036',
  'Phone332',
  'Phone5%',
  'PHONE20%',
  'PHONE25%',
  'PTNVIP',
  'REFURBYCABLE',
  'MINDER10',
  'SERVPRO5',
  'TCT',
  'ZWC1',
];

export const REPLACEMENT_DASHCAM_SKUS = ['DASHREPLACE'];

export const ASSET_SKUS = ['ZFC1AT', 'EZZFCOSPAT', 'EZZFC2AT', 'EZZFC3AT', 'EZZFCATSMP', 'EZZFCOSP2AT', 'EZZFC1AT'];

export function getSkuType(sku: string) {
  const skuUpper = sku.toUpperCase();
  switch (true) {
    case skuUpper.includes('PILOT'):
      return SKU_TYPE.PILOT;
    case skuUpper.includes('ONETIME') ||
      skuUpper.includes('ONE TIME') ||
      skuUpper.match(/^ONE\d+/g) !== null ||
      NON_DEVICE_SKUS.includes(sku):
      return SKU_TYPE.NON_DEVICE;
    case REPLACEMENT_DASHCAM_SKUS.includes(sku):
      return SKU_TYPE.DASHCAM_REPLACEMENT;
    case sku.includes('DASH'):
      return SKU_TYPE.DASHCAM;
    case sku === 'ASSETREPLACE':
      return SKU_TYPE.ASSET_REPLACEMENT;
    case isReplacement(sku):
      return SKU_TYPE.REPLACEMENT;
    case sku.includes('TRAK') || ASSET_SKUS.includes(sku):
      // this needs to happen before the "UPGRADE" type
      // - some SKUs start with EZZ
      return SKU_TYPE.ASSET;
    case sku.includes('UPGRADE'):
      return SKU_TYPE.UPGRADE;
    case NEW_ORDER_SKUS.includes(sku) || sku.startsWith('EZZ') || sku.startsWith('EZPAY') || sku.startsWith('CON500'):
      return SKU_TYPE.NEW;
    case sku.startsWith('InternalDemo') || sku.endsWith('Demo'):
      return SKU_TYPE.DEMO;
    case sku.startsWith('FZ'):
      return SKU_TYPE.TRIAL;
    default:
      const logger = useLogger();
      logger.info(`Uknown SKU type: ${sku}`);
      return SKU_TYPE.UNKNOWN;
  }
}

export function getOrderType(skus: string[]) {
  const types = skus.reduce((typesFound, sku) => {
    const skuType = getSkuType(sku);
    if (!typesFound.includes(skuType) && skuType !== SKU_TYPE.NON_DEVICE) {
      typesFound.push(skuType);
    }
    return typesFound;
  }, <SKU_TYPE[]>[]);

  if (types.length === 1) {
    return types[0];
  }

  if (types.length === 0) {
    return SKU_TYPE.ALL_NON_DEVICE;
  }

  return SKU_TYPE.MULTIPLE;
}

export function findSkuForDevice(skus: string[], device?: Device) {
  const deviceStore = useDeviceStore();
  const deviceType = deviceStore.getDeviceType(device);
  const skuTypes = skus.map((sku) => ({
    sku,
    type: exported.getSkuType(sku),
  }))

  const skusForDevice = skuTypes.filter(({ type: skuType }) => {
    const isDashcamSku = [SKU_TYPE.DASHCAM, SKU_TYPE.DASHCAM_REPLACEMENT].includes(skuType);
    const isAssetSku = [SKU_TYPE.ASSET, SKU_TYPE.ASSET_REPLACEMENT].includes(skuType);

    if (!isDashcamSku && !isAssetSku && deviceType != DEVICE_TYPE.RAVEN && deviceType != DEVICE_TYPE.ASSET) {
      return true;
    }

    if (isDashcamSku && deviceType === DEVICE_TYPE.RAVEN) {
      return true;
    }

    if (isAssetSku && deviceType === DEVICE_TYPE.ASSET) {
      return true;
    }

    return false;
  });

  return {
    skusForDevice,
    deviceType: <string>deviceType,
    skuTypes,
  }
}

export function isReplacementSku(skuType: SKU_TYPE) {
  return [SKU_TYPE.REPLACEMENT, SKU_TYPE.ASSET_REPLACEMENT, SKU_TYPE.DASHCAM_REPLACEMENT].includes(skuType);
}

const exported = {
  findSkuForDevice,
  getOrderType,
  getSkuType,
  isReplacementSku,
};

export default function useSkus() {
  return exported;
}
